import type { GrowthSummaryResponse } from '@nono/contract';
import { useViewMessages } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const number = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 1 });

const MESSAGES = {
  nb: {
    dateLocale: 'nb-NO',
    title: 'Founder scorecard',
    sub: 'Dagens medlemstall er målt nå. Historisk netto og prognose bruker sammenlignbare statuskontroller etter midnatt og åpnes først etter 28 hele Oslo-dager.',
    active: 'Aktive medlemmer',
    reconciliation: (raw: number, excluded: number, operating: number) =>
      `${raw} rå aktive − ${excluded} manuelt ekskludert = ${operating} operative aktive`,
    frozenReconciliation: (raw: number, excluded: number, operating: number) =>
      `${raw} rå frosne − ${excluded} ekskludert = ${operating} frosne, vist separat`,
    syncWarning: 'Medlemsstatus er ikke nylig bekreftet uten feil',
    identityWarning: 'Personkoblingen er ikke bekreftet av en fullført scan',
    remaining: (gap: number) => `${gap} mangler til break-even`,
    reached: 'Break-even er nådd',
    progress: (current: number, target: number) => `${current} av ${target}`,
    pace: 'Bookingfart',
    paceEvidence: (bookings: number, days: number) => `${bookings} bookinger på ${days} hele dager`,
    pacePartial: 'Historisk backfill kan mangle slettede bookinger; fart får ikke grønn status.',
    paceStale: 'Momence-bookinger er ikke nylig skannet; fart får ikke grønn status.',
    paceValue: (actual: string, target: string) => `${actual} / uke · mål ${target}`,
    onTrack: 'På mål',
    behind: 'Bak mål',
    insufficient: 'For lite grunnlag',
    flow: 'Medlemsvekst',
    flowValue: (net: number) => `${net >= 0 ? '+' : ''}${number.format(net)} netto`,
    flowSub: (start: number, end: number, days: number) =>
      `${start} → ${end} operative aktive over ${days} hele dager`,
    breakdownUnavailable:
      'Nono kan måle endringen i medlemstall, men Momence gir ikke sikre tidspunkter for nye, sluttet, tilbake, fryst og tint.',
    unavailable: 'Ikke tilgjengelig ennå',
    maturity: (done: number, required: number) =>
      `${done} av ${required} hele dagsintervaller målt`,
    rangePrecedes: 'Valgt periode starter før den nye hendelsesloggen.',
    coverageMissing: 'Valgt periode har dager uten en komplett medlemsstatus-synk.',
    truthIncomplete: 'Siste person- og bookingscan ble ikke fullført.',
    forecast: 'Break-even-prognose',
    projected: (date: string, weeks: string) => `${date} · ca. ${weeks} uker`,
    notReachable: 'Nås ikke med målt 28-dagers nettofart',
    forecastReached: 'Nådd',
    forecastWait: 'Vises først etter 28 hele dager',
    forecastSyncWait: 'Ikke tilgjengelig før medlemsstatus er nylig synket uten feil',
    forecastTruthWait: 'Ikke tilgjengelig før person- og bookingscannen er fullført',
    forecastPace: (pace: string, days: number) => `${pace} netto / uke · ${days} dager`,
    funnel: 'Målt funnel i effektiv periode',
    bookings: 'Bookinger',
    showUp: 'Oppmøte',
    closing: 'Closing',
    cancellation: 'Kansellering',
    noRate: 'Ikke målbart',
    rangeNote: (
      requestedFrom: string,
      requestedTo: string,
      from: string,
      to: string,
      completeThrough: string,
    ) =>
      `Valgt periode ${requestedFrom}–${requestedTo}. Effektiv periode ${from}–${to}; bare hele Oslo-dager brukes, til og med ${completeThrough}.`,
    quality: 'Varsler er vist som utilgjengelig eller gult — aldri som et grønt resultat.',
  },
  en: {
    dateLocale: 'en-GB',
    title: 'Founder scorecard',
    sub: 'Current membership is measured now. Historical net growth and forecasting use comparable post-midnight status checkpoints and unlock after 28 complete Oslo days.',
    active: 'Active members',
    reconciliation: (raw: number, excluded: number, operating: number) =>
      `${raw} raw active − ${excluded} manually excluded = ${operating} operating active`,
    frozenReconciliation: (raw: number, excluded: number, operating: number) =>
      `${raw} raw frozen − ${excluded} excluded = ${operating} frozen, shown separately`,
    syncWarning: 'Membership status has not been recently confirmed without errors',
    identityWarning: 'Person identity has not been confirmed by a completed scan',
    remaining: (gap: number) => `${gap} remaining to break-even`,
    reached: 'Break-even reached',
    progress: (current: number, target: number) => `${current} of ${target}`,
    pace: 'Booking pace',
    paceEvidence: (bookings: number, days: number) =>
      `${bookings} bookings across ${days} complete days`,
    pacePartial: 'Historical backfill may miss deleted bookings; pace cannot render green.',
    paceStale: 'Momence bookings have not been scanned recently; pace cannot render green.',
    paceValue: (actual: string, target: string) => `${actual} / week · target ${target}`,
    onTrack: 'On target',
    behind: 'Behind target',
    insufficient: 'Insufficient evidence',
    flow: 'Member growth',
    flowValue: (net: number) => `${net >= 0 ? '+' : ''}${number.format(net)} net`,
    flowSub: (start: number, end: number, days: number) =>
      `${start} → ${end} operating active across ${days} complete days`,
    breakdownUnavailable:
      'Nono can measure the change in member stock, but Momence does not provide reliable timestamps for joined, left, returned, frozen and unfrozen.',
    unavailable: 'Not available yet',
    maturity: (done: number, required: number) =>
      `${done} of ${required} complete daily intervals measured`,
    rangePrecedes: 'The selected period starts before the new event ledger.',
    coverageMissing: 'The selected period contains days without a complete membership-status sync.',
    truthIncomplete: 'The latest person and booking scan did not complete.',
    forecast: 'Break-even forecast',
    projected: (date: string, weeks: string) => `${date} · about ${weeks} weeks`,
    notReachable: 'Not reached at the measured 28-day net pace',
    forecastReached: 'Reached',
    forecastWait: 'Available after 28 complete days',
    forecastSyncWait: 'Unavailable until membership status is recently synced without errors',
    forecastTruthWait: 'Unavailable until the person and booking scan completes',
    forecastPace: (pace: string, days: number) => `${pace} net / week · ${days} days`,
    funnel: 'Measured funnel in the effective period',
    bookings: 'Bookings',
    showUp: 'Show-up',
    closing: 'Closing',
    cancellation: 'Cancellation',
    noRate: 'Not measurable',
    rangeNote: (
      requestedFrom: string,
      requestedTo: string,
      from: string,
      to: string,
      completeThrough: string,
    ) =>
      `Selected period ${requestedFrom}–${requestedTo}. Effective period ${from}–${to}; only complete Oslo days are used, through ${completeThrough}.`,
    quality: 'Warnings render as unavailable or amber — never as a green result.',
  },
};

function formatDay(day: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(
    new Date(`${day}T12:00:00Z`),
  );
}

function StatusChip({ status }: { status: GrowthSummaryResponse['bookingPace']['status'] }) {
  const m = useViewMessages(MESSAGES);
  const label = status === 'on_track' ? m.onTrack : status === 'behind' ? m.behind : m.insufficient;
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-micro font-semibold',
        status === 'on_track'
          ? 'bg-good-tint text-good-fg'
          : status === 'behind'
            ? 'bg-bad-tint text-bad-fg'
            : 'bg-warn-tint text-warn-fg',
      )}
    >
      {label}
    </span>
  );
}

function RateStat({
  label,
  rate,
}: {
  label: string;
  rate: GrowthSummaryResponse['funnel']['showUp'];
}) {
  const m = useViewMessages(MESSAGES);
  const measured = rate.status === 'measured' && rate.valuePct != null;
  return (
    <div>
      <div className="text-label text-fg-muted">{label}</div>
      <div className="mt-1 text-base font-bold tabular">
        {measured ? `${number.format(rate.valuePct ?? 0)} %` : '–'}
      </div>
      <div className="mt-0.5 text-micro text-fg-subtle">
        {measured ? `${rate.numerator} / ${rate.denominator}` : m.noRate}
      </div>
    </div>
  );
}

export function FounderScorecard({
  data,
  loading,
  error,
  onRetry,
}: {
  data: GrowthSummaryResponse | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  const m = useViewMessages(MESSAGES);
  if (loading) {
    return (
      <section
        className="h-56 animate-pulse rounded-xl border border-border bg-surface-2"
        aria-busy
      />
    );
  }
  if (error || !data) {
    return (
      <section className="rounded-xl border border-warn/40 bg-warn-tint p-4">
        <div className="font-semibold text-warn-fg">{m.unavailable}</div>
        <button type="button" onClick={onRetry} className="mt-2 text-xs font-semibold underline">
          {m.insufficient}
        </button>
      </section>
    );
  }

  const flowMeasured = data.membershipFlow.status === 'measured';
  const forecastText =
    data.forecast.status === 'projected' &&
    data.forecast.projectedBreakEvenDate &&
    data.forecast.weeksToBreakEven != null
      ? m.projected(
          formatDay(data.forecast.projectedBreakEvenDate, m.dateLocale),
          number.format(data.forecast.weeksToBreakEven),
        )
      : data.forecast.status === 'reached'
        ? m.forecastReached
        : data.forecast.status === 'not_reachable'
          ? m.notReachable
          : data.forecast.reasonCode === 'membership_sync_unhealthy'
            ? m.forecastSyncWait
            : data.forecast.reasonCode === 'truth_refresh_incomplete'
              ? m.forecastTruthWait
              : m.forecastWait;

  return (
    <section className="rounded-xl border border-border bg-surface p-4 shadow-card sm:p-5">
      <div>
        <h2 className="text-lg font-bold">{m.title}</h2>
        <p className="mt-0.5 max-w-4xl text-sm text-fg-subtle">{m.sub}</p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="col-span-2 rounded-lg border border-border bg-surface-2 p-3 lg:col-span-1">
          <div className="text-label text-fg-muted">{m.active}</div>
          <div className="mt-1 flex items-baseline justify-between gap-3">
            <div className="text-2xl font-bold tabular">
              {m.progress(data.membership.activeNow, data.membership.breakEvenMembers)}
            </div>
            <div className="text-xs font-semibold text-fg-muted">
              {number.format(data.membership.progressPct)} %
            </div>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-3">
            <div
              className="h-full rounded-full bg-brand"
              style={{ width: `${Math.min(100, data.membership.progressPct)}%` }}
            />
          </div>
          <div className="mt-2 text-micro text-fg-subtle">
            {data.membership.status === 'reached' ? m.reached : m.remaining(data.membership.gap)}
          </div>
          <div className="mt-1 text-micro text-fg-subtle">
            {m.reconciliation(
              data.membership.rawActiveNow,
              data.membership.excludedActiveNow,
              data.membership.activeNow,
            )}
          </div>
          <div className="mt-1 text-micro text-fg-subtle">
            {m.frozenReconciliation(
              data.membership.rawFrozenNow,
              data.membership.excludedFrozenNow,
              data.membership.frozenNow,
            )}
          </div>
          {!data.dataQuality.membershipStatusSync.healthy ? (
            <div className="mt-1 text-micro text-warn-fg">{m.syncWarning}</div>
          ) : null}
          {!data.dataQuality.truthScan.healthy ? (
            <div className="mt-1 text-micro text-warn-fg">{m.identityWarning}</div>
          ) : null}
        </div>

        <div className="rounded-lg border border-border bg-surface-2 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-label text-fg-muted">{m.pace}</div>
            <StatusChip status={data.bookingPace.status} />
          </div>
          <div className="mt-1.5 text-xl font-bold tabular">
            {m.paceValue(
              number.format(data.bookingPace.actualPerWeek),
              number.format(data.bookingPace.targetPerWeek),
            )}
          </div>
          <div className="mt-1 text-micro text-fg-subtle">
            {data.bookingPace.dataStatus === 'partial_backfill'
              ? m.pacePartial
              : data.bookingPace.dataStatus === 'stale_truth'
                ? m.paceStale
                : m.paceEvidence(data.bookingPace.bookings, data.bookingPace.evidenceDays)}
          </div>
        </div>

        <div className="rounded-lg border border-border bg-surface-2 p-3">
          <div className="text-label text-fg-muted">{m.flow}</div>
          <div
            className={cn(
              'mt-1.5 text-xl font-bold tabular',
              flowMeasured
                ? (data.membershipFlow.net ?? 0) > 0
                  ? 'text-good-fg'
                  : (data.membershipFlow.net ?? 0) === 0
                    ? 'text-warn-fg'
                    : 'text-bad-fg'
                : 'text-warn-fg',
            )}
          >
            {flowMeasured ? m.flowValue(data.membershipFlow.net ?? 0) : m.unavailable}
          </div>
          <div className="mt-1 text-micro text-fg-subtle">
            {flowMeasured
              ? m.flowSub(
                  data.membershipFlow.startActive ?? 0,
                  data.membershipFlow.endActive ?? 0,
                  Math.max(0, data.membershipFlow.checkpointCount - 1),
                )
              : data.membershipFlow.reasonCode === 'range_precedes_tracking'
                ? m.rangePrecedes
                : data.membershipFlow.reasonCode === 'tracking_coverage_incomplete'
                  ? m.coverageMissing
                  : data.membershipFlow.reasonCode === 'truth_refresh_incomplete'
                    ? m.truthIncomplete
                    : m.maturity(
                        data.membershipFlow.trackedCompleteDays,
                        data.membershipFlow.requiredCompleteDays,
                      )}
          </div>
          {flowMeasured ? (
            <div className="mt-1 text-micro text-fg-subtle">{m.breakdownUnavailable}</div>
          ) : null}
        </div>

        <div className="rounded-lg border border-border bg-surface-2 p-3">
          <div className="text-label text-fg-muted">{m.forecast}</div>
          <div
            className={cn(
              'mt-1.5 text-base font-bold leading-snug',
              data.forecast.status === 'unavailable' && 'text-warn-fg',
              data.forecast.status === 'not_reachable' && 'text-bad-fg',
            )}
          >
            {forecastText}
          </div>
          {data.forecast.netMembersPerWeek != null ? (
            <div className="mt-1 text-micro text-fg-subtle">
              {m.forecastPace(
                number.format(data.forecast.netMembersPerWeek),
                data.forecast.evidenceDays,
              )}
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-border p-3">
        <div className="text-label font-semibold text-fg-muted">{m.funnel}</div>
        <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <div className="text-label text-fg-muted">{m.bookings}</div>
            <div className="mt-1 text-base font-bold tabular">{data.funnel.bookings}</div>
            <div className="mt-0.5 text-micro text-fg-subtle">growth_booking_facts</div>
          </div>
          <RateStat label={m.showUp} rate={data.funnel.showUp} />
          <RateStat label={m.closing} rate={data.funnel.closing} />
          <RateStat label={m.cancellation} rate={data.funnel.cancellation} />
        </div>
      </div>

      <p className="mt-2 text-micro text-fg-subtle">
        {m.rangeNote(
          data.range.requested.from ?? data.range.effective.from,
          data.range.requested.to ?? data.range.effective.to,
          data.range.effective.from,
          data.range.effective.to,
          data.range.completeThrough,
        )}{' '}
        {data.dataQuality.status === 'warning' ? m.quality : ''}
      </p>
    </section>
  );
}
